import React, { useState } from 'react';
import { X, Briefcase, IdCard, UserCircle2 } from 'lucide-react';
import { useTeamMemberDetail } from '../../hooks/manager_portal_hooks';

const TABS = [
  { id: 'employment', label: 'Employment Details', icon: Briefcase },
  { id: 'position', label: 'Position Details', icon: IdCard },
];

const CONTRACT_DURATION_MAP = {
  OneMonth: '1 Month',
  TwoMonths: '2 Months',
  ThreeMonths: '3 Months',
  FourMonths: '4 Months',
  FiveMonths: '5 Months',
  SixMonths: '6 Months',
  SevenMonths: '7 Months',
  EightMonths: '8 Months',
  NineMonths: '9 Months',
  TenMonths: '10 Months',
  ElevenMonths: '11 Months',
  OneYear: '1 Year',
  OneAndHalfYear: '1.5 Years',
  TwoYears: '2 Years',
  TwoAndHalfYears: '2.5 Years',
  ThreeYears: '3 Years',
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === '1900-01-01T00:00:00Z' || dateStr === '1900-01-01T12:00:00Z') return 'N/A';
  const date = new Date(dateStr);
  if (date.getFullYear() >= 2100) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const Field = ({ label, value }) => (
  <div className="border border-gray-100 rounded-lg p-3">
    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
    <p className="text-sm text-gray-800 font-medium">{value || 'N/A'}</p>
  </div>
);

const SectionTitle = ({ icon: Icon, text, large = false }) => (
  <h3 className={`${large ? 'text-2xl font-bold' : 'text-xl font-semibold'} text-gray-800 mb-4 flex items-center gap-2`}>
    <Icon className="w-6 h-6 text-indigo-600" />
    {text}
  </h3>
);

const EmploymentTab = ({ employment, positions = [] }) => {
  if (!employment) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 text-sm">No employment data available.</p>
      </div>
    );
  }

  const contractStatus = employment.DGMA_EmploymentContractStatus
    ? employment.DGMA_EmploymentContractStatus.replace(/([A-Z])/g, '-$1').replace(/^-/, '')
    : null;

  const contractDuration = CONTRACT_DURATION_MAP[employment.DGMA_EmploymentContractDuration]
    || formatDate(employment.DGMA_EmploymentContractDuration);

  // Job Title / Start / End come from the primary position record, matching
  // PersonalInformation.jsx which reads positionsData[0].
  const primaryPosition = positions[0];

  return (
    <div>
      {/* Summary Section */}
      <div className="mb-6">
        <SectionTitle icon={Briefcase} text="Summary" large />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Employment Start Date" value={formatDate(employment.EmploymentStartDate)} />
          <Field label="Continuous Service" value={formatDate(employment.AdjustedWorkerStartDate)} />
          <Field label="Probation Period" value={employment.DGMA_EmploymentProbationPeriod} />
          <Field label="Extended Probation Date" value={formatDate(employment.DGMA_ExtendedProbationDate)} />
        </div>
      </div>

      {/* Job Details Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">Job Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Employment Contract Status" value={contractStatus} />
          <Field label="Contract Duration" value={contractDuration} />
          <Field label="Weekly Hours for Perm/Fixed term Staff" value={employment.DGMA_WeeklyHoursForPermFixedTermStaff} />
          <Field label="Job Title" value={primaryPosition?.Description} />
          <Field label="Job Start Date" value={formatDate(primaryPosition?.WorkerAssignmentStart)} />
          <Field label="Job End Date" value={formatDate(primaryPosition?.WorkerAssignmentEnd)} />
          <Field label="Terms of Employment" value={employment.DGMA_TermsOfEmployment} />
        </div>
      </div>
    </div>
  );
};

const deriveStatus = (assignmentEnd) => {
  if (!assignmentEnd) return 'Active';
  return new Date(assignmentEnd) >= new Date() ? 'Active' : 'Past';
};

const PositionTab = ({ positions }) => {
  if (!positions || positions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 text-sm">No position data available.</p>
      </div>
    );
  }

  return (
    <div>
      <SectionTitle icon={IdCard} text="Position Details" large />
      <div className="space-y-6">
        {positions.map((pos, idx) => {
          const status = deriveStatus(pos.WorkerAssignmentEnd);
          const isActive = status === 'Active';
          return (
            <div key={idx}>
              {positions.length > 1 && (
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${
                    isActive ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {status}
                  </span>
                  {idx < positions.length - 1 && (
                    <div className="flex-1 border-t border-gray-100" />
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Status" value={status} />
                <Field label="Post Number" value={pos.DGMA_PostID} />
                <Field label="Description" value={pos.Description} />
                <Field label="Primary Position" value={pos.PrimaryPosition || 'No'} />
                <Field label="Assignment Start" value={formatDate(pos.WorkerAssignmentStart)} />
                <Field
                  label="Assignment End"
                  value={pos.WorkerAssignmentEnd ? formatDate(pos.WorkerAssignmentEnd) : 'Never'}
                />
              </div>
              {idx < positions.length - 1 && (
                <div className="mt-6 border-t border-gray-200" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TeamMemberDetailModal = ({ member, onClose, initialTab = 'employment' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  const { employment, positions, isLoading, error } = useTeamMemberDetail(
    member.personnelNumber
  );

  const statusConfig = member.status === 'On Leave'
    ? { bg: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-200' }
    : { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${member.name} details`}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <UserCircle2 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{member.name}</h3>
              <p className="text-sm text-gray-500">{member.role}</p>
            </div>
            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${statusConfig.bg} ${statusConfig.text} ${statusConfig.ring}`}>
              {member.status}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 flex-shrink-0">
          <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Detail tabs">
            {TABS.map(({ id, label, icon: Icon }) => {
              const isActive = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-1.5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Scrollable content — matches PersonalInformation p-6 padding */}
        <div className="overflow-y-auto flex-1 p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
              <p className="ml-3 text-gray-600 text-sm">Loading details...</p>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-red-500 text-sm">Failed to load details. Please try again.</p>
            </div>
          ) : (
            <>
              {activeTab === 'employment' && (
                <EmploymentTab employment={employment} positions={positions} />
              )}
              {activeTab === 'position' && <PositionTab positions={positions} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamMemberDetailModal;
